import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowRight, CheckCircle, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DH_DESIGN_LOGO = "https://customer-assets.emergentagent.com/job_3ded9da4-7891-48ae-9b29-bf144d630c6e/artifacts/f6qq4dmm_1000022581.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3ded9da4-7891-48ae-9b29-bf144d630c6e/artifacts/76ag0ln1_1000021182.png";

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // BUG FIX (May 11, 2026): replaced raw fetch + response.json() with
      // axios to avoid the intermittent "body stream already read" error on
      // 4xx responses. Axios handles error bodies natively and matches the
      // pattern already used in Signup.jsx / Login.jsx / Pricing.jsx.
      const { data } = await axios.post(`${API}/api/auth/register`, {
        email,
        password,
        name,
        company: company || null,
        role: 'sales_rep',
      });

      // Check if user is pending approval
      if (data.pending) {
        setPendingApproval(true);
      } else if (data.token) {
        // First user - auto-approved, log them in
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Account created successfully');
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || error?.message || 'Registration failed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Show pending approval screen
  if (pendingApproval) {
    return (
      <div className="min-h-screen flex bg-background" data-testid="register-pending-page">
        {/* Left - Image Panel */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 flex flex-col justify-between p-12 h-full">
            <img 
              src={DH_DESIGN_LOGO} 
              alt="DH Design" 
              className="h-12 w-12 object-contain"
            />
            <div className="space-y-6">
              <h1 className="font-serif text-4xl lg:text-5xl text-white leading-tight">
                Request<br />
                <span className="text-[#C5A059]">Submitted</span>
              </h1>
              <p className="text-white/80 text-lg max-w-md leading-relaxed">
                Your access request is being reviewed by our team.
              </p>
            </div>
          </div>
        </div>

        {/* Right - Pending Message */}
        <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
          <div className="w-full max-w-md space-y-8 text-center">
            {/* Mobile Logo */}
            <div className="lg:hidden mb-8">
              <img 
                src={DH_DESIGN_LOGO} 
                alt="DH Design" 
                className="h-16 w-16 mx-auto rounded-full object-cover"
              />
            </div>

            {/* Pending Icon */}
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-10 h-10 text-amber-600" />
              </div>
            </div>

            {/* Message */}
            <div className="space-y-4">
              <h2 className="font-serif text-3xl">Access Request Submitted</h2>
              <p className="text-muted-foreground leading-relaxed">
                Your access request has been submitted and is awaiting administrator approval. 
                You will receive an email once your account has been approved.
              </p>
            </div>

            {/* Details Card */}
            <div className="bg-muted/50 rounded-lg p-6 text-left space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Account created for <strong>{email}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-amber-500" />
                <span>Pending administrator approval</span>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Already approved?{' '}
                <Link 
                  to="/login" 
                  className="text-foreground hover:text-[#B89B5E] transition-colors underline underline-offset-4"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background" data-testid="register-page">
      {/* Left - Premium Brand Panel (DH Design) */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #0a0a0a 0%, #111111 100%)',
        }}
      >
        {/* Subtle gold edge accent on the right border */}
        <div
          className="absolute inset-y-0 right-0 w-px"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(201,168,76,0.35) 50%, transparent 100%)',
          }}
        />

        <div className="relative z-10 flex flex-col justify-between p-14 xl:p-16 h-full w-full">
          {/* Top — small label */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-px" style={{ backgroundColor: '#C9A84C' }} />
            <span
              className="text-base uppercase tracking-[0.32em] font-medium"
              style={{ color: '#C9A84C' }}
            >
              Premium Sales Intelligence
            </span>
          </div>

          {/* Middle — headline + subhead */}
          <div className="space-y-7 max-w-md">
            <h1
              className="font-serif text-5xl xl:text-6xl leading-[1.05] tracking-tight"
              style={{ color: '#FFFFFF' }}
            >
              Request<br />
              <span style={{ color: '#C9A84C' }}>Platform Access</span>
            </h1>
            <div className="w-16 h-px" style={{ backgroundColor: 'rgba(201,168,76,0.6)' }} />
            <p
              className="text-base xl:text-lg leading-relaxed"
              style={{ color: '#aaaaaa', fontWeight: 300 }}
            >
              The Opportunity Intelligence Platform for reps who don&apos;t chase deals — they walk into them ready.
            </p>
          </div>

          {/* Bottom — brand anchor */}
          <div className="flex items-center gap-3">
            <span
              className="text-xl uppercase tracking-[0.3em] font-semibold"
              style={{ color: '#C9A84C' }}
            >
              — DH Design
            </span>
          </div>
        </div>
      </div>

      {/* Right - Form Panel */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md space-y-10">
          {/* Logo — top of right panel, both mobile & desktop */}
          <div className="flex justify-center lg:justify-start">
            <img
              src={DH_DESIGN_LOGO}
              alt="DH Design"
              className="h-16 w-16 object-contain"
            />
          </div>

          {/* Header */}
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Admin-Approved Waitlist
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl">Request early access</h2>
            <p className="text-muted-foreground text-sm">
              For users who want admin-approved access without a card. We review every request
              manually and grant access if approved. <span className="font-semibold text-foreground">No automatic access</span> — wait for an email from our team.
            </p>
            <div className="rounded-sm border border-[#E8E6E1] bg-white/60 backdrop-blur p-3 text-[11px] text-stone-700">
              Want immediate access?{' '}
              <Link
                to="/pricing"
                className="font-semibold underline underline-offset-2 hover:text-[#B89B5E] transition-colors"
                data-testid="register-go-to-pricing-link"
              >
                Start your 14-day free trial via pricing →
              </Link>
              <div className="text-stone-500 mt-0.5">Card required · not charged for 14 days · cancel anytime.</div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs uppercase tracking-wider">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="John Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="h-12 rounded-sm border-border bg-transparent focus:border-primary focus:ring-0 transition-colors"
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-sm border-border bg-transparent focus:border-primary focus:ring-0 transition-colors"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-xs uppercase tracking-wider">
                Company <span className="text-muted-foreground">(Optional)</span>
              </Label>
              <Input
                id="company"
                type="text"
                placeholder="Your company name"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="h-12 rounded-sm border-border bg-transparent focus:border-primary focus:ring-0 transition-colors"
                data-testid="register-company-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Min. 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 rounded-sm border-border bg-transparent focus:border-primary focus:ring-0 transition-colors"
                data-testid="register-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-wider">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-12 rounded-sm border-border bg-transparent focus:border-primary focus:ring-0 transition-colors"
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-sm uppercase tracking-widest text-xs font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting Request
                </>
              ) : (
                <>
                  Request Access
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="pt-6 border-t border-border">
            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-foreground hover:text-[#B89B5E] transition-colors underline underline-offset-4" 
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
